/**
 * 项目元素标签 API 服务
 */
import apiClient from './client';
import type {
  CreateProjectElementTagRequest,
  UpdateProjectElementTagRequest,
  ProjectElementTagResponse,
  SetSelectedResourceRequest,
  SetTagCoverImageRequest,
  SetDefaultTagRequest,
  SetDefaultTagResponse,
  ElementType,
} from '@/types/project-element-tag';

/**
 * 项目元素标签 API
 */
export const projectElementTagsApi = {
  /**
   * 创建项目元素标签
   */
  createTag: async (
    projectId: string,
    data: Omit<CreateProjectElementTagRequest, 'projectId'>
  ): Promise<ProjectElementTagResponse> => {
    return (await apiClient.post<ProjectElementTagResponse>(`/projects/${projectId}/element-tags`, {
      projectId,
      elementType: data.elementType,
      elementId: data.elementId,
      tagLabel: data.tagLabel,
      description: data.description || '',
      metaData: data.metaData || {},
      displayOrder: data.displayOrder ?? 0,
    })) as unknown as ProjectElementTagResponse;
  },

  /**
   * 获取元素的所有标签
   */
  getElementTags: async (
    projectId: string,
    elementType: ElementType,
    elementId: string
  ): Promise<ProjectElementTagResponse[]> => {
    return (await apiClient.get<ProjectElementTagResponse[]>(
      `/projects/${projectId}/element-tags/element/${elementType}/${elementId}`
    )) as unknown as ProjectElementTagResponse[];
  },

  /**
   * 获取项目的所有标签
   */
  getProjectTags: async (
    projectId: string,
    elementType?: ElementType
  ): Promise<ProjectElementTagResponse[]> => {
    const params = elementType ? { elementType } : {};
    return (await apiClient.get<ProjectElementTagResponse[]>(
      `/projects/${projectId}/element-tags`,
      {
        params,
      }
    )) as unknown as ProjectElementTagResponse[];
  },

  /**
   * 更新项目元素标签
   */
  updateTag: async (
    projectId: string,
    tagId: string,
    data: UpdateProjectElementTagRequest
  ): Promise<ProjectElementTagResponse> => {
    return (await apiClient.put<ProjectElementTagResponse>(
      `/projects/${projectId}/element-tags/${tagId}`,
      data
    )) as unknown as ProjectElementTagResponse;
  },

  /**
   * 设置标签的定稿素材
   */
  setSelectedResource: async (
    projectId: string,
    tagId: string,
    data: SetSelectedResourceRequest
  ): Promise<ProjectElementTagResponse> => {
    return (await apiClient.put<ProjectElementTagResponse>(
      `/projects/${projectId}/element-tags/${tagId}/selected-resource`,
      data
    )) as unknown as ProjectElementTagResponse;
  },

  /**
   * 设置标签封面图
   */
  setTagCoverImage: async (
    projectId: string,
    tagId: string,
    data: SetTagCoverImageRequest
  ): Promise<ProjectElementTagResponse> => {
    return (await apiClient.put<ProjectElementTagResponse>(
      `/projects/${projectId}/element-tags/${tagId}/cover-image`,
      data
    )) as unknown as ProjectElementTagResponse;
  },

  /**
   * 删除项目元素标签
   */
  deleteTag: async (projectId: string, tagId: string): Promise<void> => {
    await apiClient.delete(`/projects/${projectId}/element-tags/${tagId}`);
  },

  /**
   * 设置元素的默认标签
   */
  setDefaultTag: async (
    projectId: string,
    elementType: ElementType,
    elementId: string,
    data: SetDefaultTagRequest
  ): Promise<SetDefaultTagResponse> => {
    return (await apiClient.put<SetDefaultTagResponse>(
      `/projects/${projectId}/elements/${elementType}/${elementId}/default-tag`,
      data
    )) as unknown as SetDefaultTagResponse;
  },
};
